import { resolveContainerLabel } from 'utils/activity/customBuilders/workOrderContainerLinkBuilder'

import type { WorkOrderStatusEventApi } from 'types/api/work-order/workOrderStatus'
import type ActivityTreeBuilder from 'utils/activity/ActivityTreeBuilder'

type WorkOrderContainer = { id?: string }

export type StatusEventBuilderPayload = {
  statusEvents?: WorkOrderStatusEventApi[]
  workOrderContainers?: WorkOrderContainer[]
}

// Events arrive newest first, so the prior status is the next matching row down the list.
const previousCompletionStatus = (events: WorkOrderStatusEventApi[], index: number): string => {
  const { containerId } = events[index]
  for (let i = index + 1; i < events.length; i += 1) {
    if (events[i].containerId === containerId && events[i].completionStatus) return events[i].completionStatus ?? ''
  }
  return ''
}

/**
 * Adds per-container completion and auto-complete events to the WO activity tree. They live in the
 * insert-only work_order_status_event table, which Envers never sees.
 */
export default function statusEventBuilder(builder: ActivityTreeBuilder, payload?: unknown): void {
  const { statusEvents = [], workOrderContainers = [] } = (payload as StatusEventBuilderPayload) || {}
  if (!Array.isArray(statusEvents) || statusEvents.length === 0) return

  const containerById = Object.fromEntries(
    workOrderContainers.filter(container => container?.id).map(container => [container.id, container])
  )

  statusEvents.forEach((event, index) => {
    if (!event?.containerId || !(event.completionStatus || event.autoCompleteStatus)) return

    const label = resolveContainerLabel(event, containerById)
    const version = { updatedAt: event.eventTime || event.createdAt || undefined, lastModifiedBy: event.createdBy || undefined }
    const rev = `status-${event.id || index}`

    if (event.completionStatus) {
      builder.addCustomChange(rev, version, {
        field: `Container status · ${label}`,
        fieldKey: 'containerCompletionStatus',
        oldValue: previousCompletionStatus(statusEvents, index),
        newValue: event.completionStatus,
        changeType: 'Updated',
      })
    }
    // An auto-serviced event carries both fields on one row, so this is not an else.
    if (event.autoCompleteStatus) {
      builder.addCustomChange(rev, version, {
        field: `Auto-complete · ${label}`,
        fieldKey: 'containerAutoCompleteStatus',
        oldValue: '',
        newValue: event.autoCompleteStatus,
        changeType: 'Updated',
      })
    }
  })
}
